import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AlertCircle, TrendingUp, Brain, Zap } from "lucide-react";
import GoogleLoginButton from "@/components/GoogleLoginButton";

const L = {
  bg: { hero: "AI-базиран метаболитен и хранителен анализ", sub: "Разберете тялото си. Оптимизирайте начина си на живот.", desc: "Получете персонализирани анализи за хранене, метаболизъм и дневни навици чрез напреднал AI анализ.", p1: "Метаболитни анализи", p2: "AI Анализ", p3: "Умни съвети", email: "Имейл", pass: "Парола", login: "Вход", logging: "Влизане...", noAccount: "Нямате акаунт?", create: "Създайте", disclaimer: "Тази платформа предоставя само информационни анализи и не замества професионален медицински съвет, диагноза или лечение.", forgot: "Забравена парола?", or: "или", google: "Вход с Google", magicLink: "Вход с линк по имейл (без парола)" },
  en: { hero: "AI-powered metabolic & nutrition intelligence", sub: "Understand your body. Optimize your lifestyle.", desc: "Get personalized insights about your nutrition, metabolism, and daily habits using advanced AI analysis.", p1: "Metabolic Insights", p2: "AI Analysis", p3: "Smart Tips", email: "Email", pass: "Password", login: "Sign in", logging: "Signing in...", noAccount: "Don't have an account?", create: "Create one", disclaimer: "This platform provides informational insights only and does not replace professional medical advice, diagnosis, or treatment.", forgot: "Forgot password?", or: "or", google: "Continue with Google", magicLink: "Email me a one-click sign-in link" },
  es: { hero: "Inteligencia metabolica y nutricional con IA", sub: "Comprenda su cuerpo. Optimice su estilo de vida.", desc: "Obtenga analisis personalizados sobre nutricion y metabolismo.", p1: "Analisis metabolico", p2: "Analisis IA", p3: "Consejos inteligentes", email: "Correo", pass: "Contrasena", login: "Iniciar sesion", logging: "Iniciando...", noAccount: "No tiene cuenta?", create: "Crear una", disclaimer: "Esta plataforma solo proporciona informacion y no reemplaza el consejo medico profesional.", forgot: "Contrasena olvidada?", or: "o", google: "Continuar con Google", magicLink: "Enviarme un enlace de inicio sin contrasena" },
};

export default function Login() {
  const { login, verifyTwoFactor } = useAuth();
  const { language, setLanguage, SUPPORTED_LANGUAGES } = useLanguage();
  const l = L[language] || L.en;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // 2FA challenge state
  const [challengeId, setChallengeId] = useState(null);
  const [otp, setOtp] = useState("");

  const navigate = useNavigate();
  const bg = language === "bg";
  const isEs = language === "es";
  const tt = (bgT, enT, esT) => bg ? bgT : isEs ? (esT || enT) : enT;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.twoFactorRequired) {
        setChallengeId(res.challengeId);
        return;
      }
      // iter501 — Explicit navigate immediately after successful login.
      // Previously we relied on PublicRoute auto-redirect via setUser → re-render,
      // which sometimes took 1-2 extra seconds + briefly showed the loading
      // spinner, requiring user to hard-refresh. Direct navigate fixes this.
      const hasProfile = res?.profile?.completedOnboarding === true;
      const isClinicianPending = res?.role === "clinician" && res?.clinician_status !== "approved";
      if (isClinicianPending) {
        navigate("/clinician-pending", { replace: true });
      } else if (!hasProfile && res?.role !== "clinician") {
        navigate("/onboarding", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || "";
      if (status === 403 && (detail.includes("не е потвърден") || detail.includes("not verified"))) {
        navigate(`/register?verify=${encodeURIComponent(email)}`);
        return;
      }
      setError(formatApiErrorDetail(detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await verifyTwoFactor(challengeId, otp);
      // iter501 — Explicit navigate after 2FA success (same fix as primary login).
      const hasProfile = data?.profile?.completedOnboarding === true;
      if (!hasProfile && data?.role !== "clinician") {
        navigate("/onboarding", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-[#FAFBFC] to-[#F0FDF4] flex flex-col">
      {/* Language Dropdown */}
      <div className="absolute top-4 right-4 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="login-language-toggle"
              className="rounded-lg bg-white/80 backdrop-blur-sm border-[#E2E8F0] hover:bg-white text-sm font-bold">
              {language.toUpperCase()}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <DropdownMenuItem key={lang.code} onClick={() => setLanguage(lang.code)}
                data-testid={`login-lang-${lang.code}`}
                className={`cursor-pointer ${language === lang.code ? 'text-[#0EA5E9] bg-blue-50' : ''}`}>
                <span className="text-xs font-bold w-6">{lang.label}</span>
                <span className="text-sm">{lang.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          {/* Logo (wordmark вграден в PNG) */}
          <div className="text-center mb-6 sm:mb-8">
            <img src="/nutramilo-logo.png" alt="Nutramilo" className="mx-auto h-14 sm:h-16 w-auto object-contain" />
          </div>

          {/* Hero */}
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A2E] mb-2 sm:mb-3 leading-tight">{l.hero}</h2>
            <p className="text-base sm:text-lg text-[#64748B] mb-2">{l.sub}</p>
            <p className="text-xs sm:text-sm text-[#94A3B8] max-w-sm mx-auto">{l.desc}</p>
          </div>

          {/* Feature Pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-6 sm:mb-8">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border border-[#E2E8F0] text-xs text-[#64748B]">
              <TrendingUp className="w-3.5 h-3.5 text-[#0EA5E9]" />{l.p1}
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border border-[#E2E8F0] text-xs text-[#64748B]">
              <Brain className="w-3.5 h-3.5 text-[#6366F1]" />{l.p2}
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border border-[#E2E8F0] text-xs text-[#64748B]">
              <Zap className="w-3.5 h-3.5 text-[#10B981]" />{l.p3}
            </div>
          </div>

          {/* Login Card */}
          <Card className="card-elevated border-0">
            <CardContent className="p-6">
              {challengeId ? (
                <form onSubmit={handleVerify2FA} className="space-y-4" data-testid="twofa-login-form">
                  {error && (
                    <div className="flex items-center space-x-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm" data-testid="login-error">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
                    </div>
                  )}
                  <div className="text-sm text-[#1A1A2E]">
                    {tt("Изпратихме 6-цифрен код на ", "We sent a 6-digit code to ", "Hemos enviado un codigo de 6 digitos a ")}
                    <strong>{email}</strong>. {tt("Валиден 5 мин.", "Valid for 5 min.", "Valido 5 min.")}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="otp" className="text-[#1A1A2E] text-sm font-medium">{tt("Код", "Code", "Codigo")}</Label>
                    <Input id="otp" type="text" inputMode="numeric" pattern="\d{6}" maxLength={6}
                      placeholder="000000" value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      required data-testid="twofa-otp-input"
                      className="h-11 bg-[#F8FAFC] border-[#E2E8F0] focus:ring-[#0EA5E9] focus:border-[#0EA5E9] rounded-xl tracking-[0.5em] text-center text-lg" />
                  </div>
                  <Button type="submit" disabled={loading || otp.length !== 6} data-testid="twofa-submit-btn"
                    className="w-full h-11 gradient-primary hover:opacity-90 text-white font-medium rounded-xl shadow-lg shadow-sky-500/20">
                    {loading ? tt("Проверка…", "Verifying…", "Verificando…") : tt("Потвърди", "Verify", "Verificar")}
                  </Button>
                  <button type="button" onClick={() => { setChallengeId(null); setOtp(""); setError(""); }}
                    className="w-full text-xs text-[#64748B] hover:text-[#0EA5E9]" data-testid="twofa-back-btn">
                    ← {tt("Обратно към вход", "Back to login", "Volver al inicio")}
                  </button>
                </form>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center space-x-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm" data-testid="login-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1A1A2E] text-sm font-medium">{l.email}</Label>
                  <Input id="email" type="email" placeholder="you@example.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input"
                    className="h-11 bg-[#F8FAFC] border-[#E2E8F0] focus:ring-[#0EA5E9] focus:border-[#0EA5E9] rounded-xl" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-[#1A1A2E] text-sm font-medium">{l.pass}</Label>
                    <Link to="/forgot-password" className="text-xs text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-testid="forgot-password-link">{l.forgot}</Link>
                  </div>
                  <Input id="password" type="password" placeholder="••••••••" value={password}
                    onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input"
                    className="h-11 bg-[#F8FAFC] border-[#E2E8F0] focus:ring-[#0EA5E9] focus:border-[#0EA5E9] rounded-xl" />
                </div>
                <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                  className="w-full h-11 gradient-primary hover:opacity-90 text-white font-medium rounded-xl shadow-lg shadow-sky-500/20">
                  {loading ? l.logging : l.login}
                </Button>
              </form>
              )}

              {/* Magic-link (passwordless) — sends one-click sign-in to email */}
              <div className="mt-3">
                <Link
                  to="/magic-link"
                  data-testid="magic-link-cta"
                  className="block w-full text-center text-sm font-semibold py-2.5 rounded-xl border border-[#0EA5E9]/30 text-[#0EA5E9] hover:bg-[#0EA5E9]/5 transition-colors"
                >
                  {l.magicLink || "Влез с линк по имейл (без парола)"}
                </Link>
              </div>

              {/* Feb 2026 — Emergent-managed Google Auth (co-exists with JWT login).
                  REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-[#E2E8F0]" />
                <span className="text-xs text-[#94A3B8] font-medium">{l.or || "or"}</span>
                <div className="flex-1 h-px bg-[#E2E8F0]" />
              </div>
              <GoogleLoginButton label={l.google || "Continue with Google"} />

              <div className="mt-6 text-center text-sm text-[#64748B]">
                {l.noAccount}{" "}
                <Link to="/register" className="text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-testid="register-link">{l.create}</Link>
              </div>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-xs text-[#94A3B8] max-w-sm mx-auto leading-relaxed">{l.disclaimer}</p>
        </div>
      </div>
    </div>
  );
}
